/**
 * WeaveIn.ai Platform - Field Import Dialog
 * Allows importing fields from unified field configuration into form builder
 */

import React, { useState, useEffect } from 'react';
import { Download, Search, Info, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useCustomFields from '@/hooks/useCustomFields';
import useFieldGroups from '@/hooks/useFieldGroups';
import { UnifiedFieldService, UnifiedField } from '@/services/unifiedFields';
import { FormFieldSchema } from '@/services/api/forms';

import { useAppTranslation } from '@/contexts/TranslationContext';

interface FieldImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (fields: FormFieldSchema[]) => void;
}

type RecordTypeFilter = 'all' | 'member' | 'contact';

export const FieldImportDialog: React.FC<FieldImportDialogProps> = ({ open, onClose, onImport }) => {
  const { t } = useAppTranslation();
  const [recordTypeFilter, setRecordTypeFilter] = useState<RecordTypeFilter>('all');
  const { fields: customFields, loading, error } = useCustomFields({
    autoFetch: true,
    include_hidden: false,
    record_type: recordTypeFilter === 'all' ? undefined : recordTypeFilter,
  });
  const { groups: fieldGroups } = useFieldGroups();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set());
  const [allFields, setAllFields] = useState<UnifiedField[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState('all');

  useEffect(() => {
    if (!loading && customFields) {
      const unified = UnifiedFieldService.getAllFields(customFields);
      setAllFields(unified);
      setCategories(['all', ...UnifiedFieldService.getAllCategories(customFields)]);
    }
  }, [customFields, loading]);

  const filteredFields = allFields.filter((field) => {
    // Filter by category
    if (selectedCategory !== 'all' && field.category !== selectedCategory) {
      return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        field.name.toLowerCase().includes(query) ||
        field.friendly_name.toLowerCase().includes(query) ||
        field.category.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const coreFields = filteredFields.filter((f) => f.is_core);
  const customFieldsFiltered = filteredFields.filter((f) => !f.is_core);

  const handleToggleField = (fieldName: string) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(fieldName)) {
      newSelected.delete(fieldName);
    } else {
      newSelected.add(fieldName);
    }
    setSelectedFields(newSelected);
  };

  const handleSelectAll = (fields: UnifiedField[]) => {
    const newSelected = new Set(selectedFields);
    fields.forEach((field) => newSelected.add(field.name));
    setSelectedFields(newSelected);
  };

  const handleDeselectAll = (fields: UnifiedField[]) => {
    const newSelected = new Set(selectedFields);
    fields.forEach((field) => newSelected.delete(field.name));
    setSelectedFields(newSelected);
  };

  const handleImport = () => {
    const fieldsToImport: FormFieldSchema[] = [];
    let order = 0;

    selectedFields.forEach((fieldName) => {
      const field = allFields.find((f) => f.name === fieldName);
      if (field) {
        const formField = UnifiedFieldService.convertToFormField(field);
        fieldsToImport.push({
          ...formField,
          order,
        });
        order++;
      }
    });

    // Append field-group placeholders. The renderer uses field_group_id to look
    // up the live group and expand it into N instances at runtime.
    selectedGroups.forEach((gid) => {
      const group = fieldGroups.find((g) => g.id === gid);
      if (!group) return;
      fieldsToImport.push({
        field_id: group.name, // submitted data lives under field_data[group_name]
        field_type: 'field_group' as any,
        label: group.friendly_name,
        description: group.description ?? undefined,
        required: false,
        order,
        field_group_id: gid,
      } as any);
      order++;
    });

    onImport(fieldsToImport);
    setSelectedFields(new Set());
    setSelectedGroups(new Set());
    onClose();
  };

  const handleToggleGroup = (gid: number) => {
    const next = new Set(selectedGroups);
    if (next.has(gid)) next.delete(gid);
    else next.add(gid);
    setSelectedGroups(next);
  };

  const renderFieldList = (fields: UnifiedField[], title: string) => {
    if (fields.length === 0) return null;

    const allSelected = fields.every((f) => selectedFields.has(f.name));
    const someSelected = fields.some((f) => selectedFields.has(f.name)) && !allSelected;

    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {title} ({fields.length})
          </h3>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleSelectAll(fields)}
              disabled={allSelected}
            >
              {t('forms.select_all', 'Select All')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDeselectAll(fields)}
              disabled={!someSelected && !allSelected}
            >
              {t('forms.deselect_all', 'Deselect All')}
            </Button>
          </div>
        </div>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-[300px] overflow-auto">
          {fields.map((field, index) => (
            <div
              key={field.name}
              className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                index !== fields.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
              }`}
              onClick={() => handleToggleField(field.name)}
            >
              <Checkbox
                checked={selectedFields.has(field.name)}
                onCheckedChange={() => handleToggleField(field.name)}
                onClick={(e) => e.stopPropagation()}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {field.friendly_name}
                  </span>
                  {field.mandatory && (
                    <Badge variant="danger" size="sm">
                      {t('forms.required', 'Required')}
                    </Badge>
                  )}
                  {field.is_core && (
                    <Badge variant="primary" size="sm">
                      {t('forms.core', 'Core')}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {field.name} • {field.type}
                  {field.category && ` • ${field.category}`}
                  {field.field_options && field.field_options.length > 0 && ` • ${field.field_options.length} options`}
                </div>
                {field.field_options && field.field_options.length > 0 && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex flex-wrap gap-1">
                    {field.field_options.slice(0, 5).map((opt) => (
                      <span key={opt.value} className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                        {opt.label}
                      </span>
                    ))}
                    {field.field_options.length > 5 && (
                      <span className="text-gray-400">+{field.field_options.length - 5} more</span>
                    )}
                  </div>
                )}
                {field.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {field.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        zIndex={10000}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            {t('forms.import_fields_from_configuration', 'Import Fields from Configuration')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto min-h-0">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Import fields from your field configuration to quickly build forms. Selected fields will be added to
                  the current section with their validation rules and properties.
                </AlertDescription>
              </Alert>

              {/* Record-type scope (members/contacts are stored in the same table;
                  this filter narrows to fields whose applies_to_record_types matches). */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-300">Scope:</span>
                <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {(['all', 'member', 'contact'] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setRecordTypeFilter(opt)}
                      className={`px-3 py-1 text-xs font-medium ${
                        opt !== 'all' ? 'border-l border-gray-200 dark:border-gray-700' : ''
                      } ${
                        recordTypeFilter === opt
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {opt === 'all' ? 'All' : opt === 'member' ? 'Member fields' : 'Contact fields'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={t('forms.search_fields', 'Search fields...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="w-48">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('forms.category', 'Category')} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat === 'all' ? 'All Categories' : cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="all" className="w-full" value={tabValue} onValueChange={setTabValue}>
                <TabsList className="flex border-b border-gray-200 dark:border-gray-700 w-full justify-start">
                  <TabsTrigger
                    value="all"
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 transition-colors"
                  >
                    All Fields ({filteredFields.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="core"
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 transition-colors"
                  >
                    Core Fields ({coreFields.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="custom"
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 transition-colors"
                  >
                    Custom Fields ({customFieldsFiltered.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="groups"
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 transition-colors"
                  >
                    Field Groups ({fieldGroups.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="mt-4">
                  {renderFieldList(coreFields, 'Core Fields')}
                  {renderFieldList(customFieldsFiltered, 'Custom Fields')}
                </TabsContent>

                <TabsContent value="core" className="mt-4">
                  {renderFieldList(coreFields, 'Core Fields')}
                </TabsContent>

                <TabsContent value="custom" className="mt-4">
                  {renderFieldList(customFieldsFiltered, 'Custom Fields')}
                </TabsContent>

                <TabsContent value="groups" className="mt-4">
                  {fieldGroups.length === 0 ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        No field groups defined yet. Create one under{' '}
                        <span className="font-medium">Member Profile Configuration → Field Groups</span>{' '}
                        to use it here.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-[300px] overflow-auto">
                      {fieldGroups.map((g, idx) => (
                        <div
                          key={g.id}
                          className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                            idx !== fieldGroups.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                          }`}
                          onClick={() => handleToggleGroup(g.id)}
                        >
                          <Checkbox
                            checked={selectedGroups.has(g.id)}
                            onCheckedChange={() => handleToggleGroup(g.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {g.friendly_name}
                              </span>
                              {g.is_repeatable ? (
                                <Badge variant="primary" size="sm">Repeatable</Badge>
                              ) : (
                                <Badge variant="secondary" size="sm">Single</Badge>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {g.name} • {g.children.length} field{g.children.length === 1 ? '' : 's'}
                              {g.min_instances != null || g.max_instances != null
                                ? ` • ${g.min_instances ?? 0}–${g.max_instances ?? '∞'} instances`
                                : ''}
                            </div>
                            {g.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {g.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {filteredFields.length === 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t('forms.no_fields_found_matching_your', 'No fields found matching your filters.')}
                  </AlertDescription>
                </Alert>
              )}

              {(selectedFields.size > 0 || selectedGroups.size > 0) && (
                <Alert className="bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700">
                  <AlertDescription className="text-green-700 dark:text-green-300">
                    {selectedFields.size} field{selectedFields.size === 1 ? '' : 's'}
                    {selectedGroups.size > 0 && (
                      <>
                        {selectedFields.size > 0 ? ' and ' : ''}
                        {selectedGroups.size} group{selectedGroups.size === 1 ? '' : 's'}
                      </>
                    )}
                    {' '}selected
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="ghost" onClick={onClose}>
            {t('forms.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={(selectedFields.size === 0 && selectedGroups.size === 0) || loading}
            icon={<Download className="w-4 h-4" />}
            iconPosition="left"
          >
            Import {selectedFields.size + selectedGroups.size > 0 ? `${selectedFields.size + selectedGroups.size} ` : ''}
            {selectedFields.size + selectedGroups.size === 1 ? 'Item' : 'Items'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
